import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { SessionEndDiagnosticsService } from '../services/auth/session-end-diagnostics.service';
import { HttpStatus } from '../utils/http-status.util';

/**
 * Single-active-session-per-device enforcement (default `OnNextApiCall` mode).
 *
 * The backend now allows only one live session per user. Logging in on a new
 * device/browser invalidates the previous one; the displaced device is signed
 * out on its **next API call**, which comes back as:
 *
 *   - HTTP 401
 *   - header `X-Auth-Reason: session-superseded`
 *   - body `{ ..., message: "SESSION_SUPERSEDED" }`
 *
 * This interceptor is the single global seam that turns that signal into a
 * forced UI logout. A plain 401 (no superseded marker) on a previously
 * authenticated call means the cookie is simply gone/expired — we still force
 * a logout, but with the ordinary "session expired" copy.
 *
 * Deliberate non-responsibilities (kept here so the seam stays obvious):
 *   - **Auth endpoints** (`/api/auth/*`) are skipped. A 401 there is a
 *     credential failure (wrong password) or the bootstrap `is-authenticated`
 *     probe — `LoginComponent` / the guards own those, not us. The backend's
 *     `SessionValidationMiddleware` also `[AllowAnonymous]`-bypasses these, so
 *     a displaced user can still reach `POST /auth/login` to sign back in.
 *   - **Dev (Bearer/JWT) sessions** are skipped via the `isDev()` gate. Their
 *     401s are owned by `devJwtInterceptor` → `DevSessionService.markExpired()`.
 *   - **Unauthenticated callers** are skipped — a stray 401 before auth has
 *     resolved must not bounce a never-logged-in visitor.
 *
 * No auto-retry: a 401 on an authenticated session means "you are out", not
 * "try again". We re-throw the error untouched so existing per-call
 * `catchError` handlers still run.
 */
export const sessionExpiryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const diagnostics = inject(SessionEndDiagnosticsService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpStatus.Unauthorized) {
        // Developer Note (TODO): the `X-Auth-Reason` response header is exposed
        // to the browser (BE CORS `Access-Control-Expose-Headers`) only as a
        // DIAGNOSTIC aid for the AS-569 stuck-page investigation. Once the
        // stale-vs-superseded root cause is identified and resolved, coordinate
        // with the backend to STOP exposing it (trim it from
        // `Access-Control-Expose-Headers`) so we don't leak auth internals to
        // the client long-term. The body marker (`message: "SESSION_SUPERSEDED"`)
        // read just below is the durable signal and keeps this interceptor
        // working after the header is removed — verify that fallback before the
        // BE drops the header.
        const headerReason = error.headers?.get('X-Auth-Reason') ?? null;
        const superseded =
          headerReason === 'session-superseded' ||
          (error.error as { message?: string } | null)?.message ===
            'SESSION_SUPERSEDED';
        const path = safePath(req.url);

        // Only customer (cookie) calls to non-auth endpoints are ours to act
        // on; dev/Bearer 401s belong to devJwtInterceptor and /api/auth/* 401s
        // belong to the login flow/guards.
        if (authService.isDev() !== true && !path.startsWith('/api/auth/')) {
          const willForceLogout = shouldForceLogout(authService, superseded);

          // One breadcrumb per gated 401 — including the gate-SKIPS, which are
          // the silent failure mode behind the "stuck on Conversion Listing"
          // report. Correlates with the backend's ApplicationLog `Verdict` row.
          diagnostics.record({
            source: 'next-api-call-401',
            method: req.method,
            path,
            route: location.pathname,
            authReason:
              headerReason ?? (superseded ? 'SESSION_SUPERSEDED' : null),
            action: willForceLogout ? 'force-logout' : 'gate-skipped',
          });

          if (willForceLogout) {
            authService.forceLogout(superseded ? 'session-superseded' : 'session-expired');
          }
        }
      }

      // Re-throw so individual handlers still see the error. No retry.
      return throwError(() => error);
    })
  );
};

/** Resolve the request's pathname, tolerating relative URLs. */
function safePath(url: string): string {
  try {
    return new URL(url, location.origin).pathname;
  } catch {
    return url;
  }
}

/**
 * Whether a 401 on a non-auth, non-dev call should force a UI logout.
 *
 * Normal case: a previously authenticated cookie session (`isAuthenticated`
 * signal is `true`).
 *
 * Fallback case (AS-569 stuck-page fix): the backend only stamps a request
 * `SESSION_SUPERSEDED` when it has actually torn down a live server session, so
 * that marker is AUTHORITATIVE — honour it even if our in-memory signal was
 * already flipped to `false` by an earlier teardown on the same still-mounted
 * page. Without this, a second 401 arriving after the signal flipped was
 * silently gate-skipped (and then swallowed by the page's own `catchError`),
 * leaving the user on a dead page. We require an explicit `false`: a `null`
 * means auth has not resolved yet (bootstrap), where a stray 401 must NOT bounce
 * a never-logged-in visitor. A genuinely anonymous visitor never receives the
 * marker (there is no session to supersede), so this cannot misfire on them.
 */
function shouldForceLogout(
  authService: AuthService,
  superseded: boolean
): boolean {
  if (authService.isAuthenticated() === true) {
    return true;
  }
  if (superseded && authService.isAuthenticated() === false) {
    return true;
  }
  return false;
}
